import {
  Building2,
  TrendingUp,
  TrendingDown,
  Package,
  DollarSign,
  Users,
  Target,
  Settings,
} from 'lucide-react'
import { useState } from 'react'
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  Cell,
} from 'recharts'

import { Button } from '../components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog'
import { Input } from '../components/ui/input'

const branches = [
  {
    id: 1,
    name: 'Sucursal Centro',
    address: 'Av. Principal #123, Centro',
    manager: 'María López',
  },
  {
    id: 2,
    name: 'Sucursal Norte',
    address: 'Blvd. Norte #456, Zona Norte',
    manager: 'Carlos Méndez',
  },
  {
    id: 3,
    name: 'Sucursal Sur',
    address: 'Calle Sur #789, Zona Sur',
    manager: 'Ana Torres',
  },
]

const branchMetrics = [
  {
    branch: 'Sucursal Centro',
    sales: 156000,
    profit: 45800,
    customers: 420,
    products: 1250,
    growth: 12.5,
  },
  {
    branch: 'Sucursal Norte',
    sales: 138000,
    profit: 38900,
    customers: 380,
    products: 1120,
    growth: 8.2,
  },
  {
    branch: 'Sucursal Sur',
    sales: 142000,
    profit: 41200,
    customers: 395,
    products: 1180,
    growth: -3.1,
  },
]

const salesComparison = [
  { month: 'Ene', Centro: 145000, Norte: 125000, Sur: 132000 },
  { month: 'Feb', Centro: 152000, Norte: 135000, Sur: 138000 },
  { month: 'Mar', Centro: 148000, Norte: 132000, Sur: 145000 },
  { month: 'Abr', Centro: 156000, Norte: 138000, Sur: 142000 },
]

const profitComparison = [
  { month: 'Ene', Centro: 42000, Norte: 35000, Sur: 38000 },
  { month: 'Feb', Centro: 44500, Norte: 37800, Sur: 39500 },
  { month: 'Mar', Centro: 43200, Norte: 36900, Sur: 41200 },
  { month: 'Abr', Centro: 45800, Norte: 38900, Sur: 41200 },
]

const topProductsByBranch = [
  { branch: 'Centro', product: 'Paracetamol 500mg', units: 520 },
  { branch: 'Centro', product: 'Ibuprofeno 400mg', units: 480 },
  { branch: 'Norte', product: 'Loratadina 10mg', units: 450 },
  { branch: 'Norte', product: 'Omeprazol 20mg', units: 420 },
  { branch: 'Sur', product: 'Amoxicilina 500mg', units: 410 },
  { branch: 'Sur', product: 'Paracetamol 500mg', units: 380 },
]

const initialSalesGoals = [
  {
    branch: 'Sucursal Centro',
    dailyGoal: 6000,
    dailyActual: 6500,
    monthlyGoal: 150000,
    monthlyActual: 156000,
    annualGoal: 1800000,
    annualActual: 1650000,
  },
  {
    branch: 'Sucursal Norte',
    dailyGoal: 5500,
    dailyActual: 5200,
    monthlyGoal: 140000,
    monthlyActual: 138000,
    annualGoal: 1680000,
    annualActual: 1580000,
  },
  {
    branch: 'Sucursal Sur',
    dailyGoal: 5800,
    dailyActual: 6100,
    monthlyGoal: 145000,
    monthlyActual: 142000,
    annualGoal: 1740000,
    annualActual: 1620000,
  },
]

export function MultiBranch() {
  const [salesGoals, setSalesGoals] = useState(initialSalesGoals)
  const [showGoalsModal, setShowGoalsModal] = useState(false)
  const [editingGoals, setEditingGoals] = useState(initialSalesGoals)

  const goalsComparison = salesGoals.map((goal) => ({
    branch: goal.branch.replace('Sucursal ', ''),
    Meta: goal.monthlyGoal,
    Real: goal.monthlyActual,
  }))

  const handleSaveGoals = () => {
    setSalesGoals(editingGoals)
    setShowGoalsModal(false)
  }
  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">
            Gestión Multi-sucursal
          </h1>
          <p className="mt-1 text-sm text-slate-600">
            Vista consolidada de {branches.length} sucursales
          </p>
        </div>
        <Button
          onClick={() => {
            setEditingGoals(salesGoals)
            setShowGoalsModal(true)
          }}
          variant="outline"
        >
          <Settings className="mr-2 h-4 w-4" />
          Configurar Metas
        </Button>
      </div>

      {/* Branch Cards */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
        {branches.map((branch, index) => {
          const metrics = branchMetrics[index]
          const goals = salesGoals[index]
          const dailyProgress = (goals.dailyActual / goals.dailyGoal) * 100
          const monthlyProgress =
            (goals.monthlyActual / goals.monthlyGoal) * 100
          const annualProgress = (goals.annualActual / goals.annualGoal) * 100

          return (
            <div
              key={branch.id}
              className="rounded-lg border border-slate-200 bg-white p-6"
            >
              <div className="mb-4 flex items-start justify-between">
                <div>
                  <div className="mb-2 flex items-center gap-2">
                    <Building2 className="h-5 w-5 text-primary" />
                    <h3 className="font-semibold text-slate-900">
                      {branch.name}
                    </h3>
                  </div>
                  <p className="text-xs text-slate-600">{branch.address}</p>
                  <p className="mt-1 text-xs text-slate-600">
                    Encargado:{' '}
                    <span className="font-medium">{branch.manager}</span>
                  </p>
                </div>
              </div>

              <div className="mt-6 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">
                    Ventas del mes:
                  </span>
                  <span className="font-semibold text-slate-900">
                    ${metrics.sales.toLocaleString()}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">Utilidad:</span>
                  <span className="font-semibold text-green-600">
                    ${metrics.profit.toLocaleString()}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">Clientes:</span>
                  <span className="font-medium text-slate-900">
                    {metrics.customers}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">
                    Productos vendidos:
                  </span>
                  <span className="font-medium text-slate-900">
                    {metrics.products}
                  </span>
                </div>
                <div className="flex items-center justify-between border-t border-slate-200 pt-2">
                  <span className="text-sm text-slate-600">Crecimiento:</span>
                  <div className="flex items-center gap-1">
                    {metrics.growth > 0 ? (
                      <TrendingUp className="h-4 w-4 text-green-600" />
                    ) : (
                      <TrendingDown className="h-4 w-4 text-red-600" />
                    )}
                    <span
                      className={`font-semibold ${
                        metrics.growth > 0 ? 'text-green-600' : 'text-red-600'
                      }`}
                    >
                      {metrics.growth > 0 ? '+' : ''}
                      {metrics.growth}%
                    </span>
                  </div>
                </div>
              </div>

              {/* Sales Goals Section */}
              <div className="mt-4 space-y-3 border-t border-slate-200 pt-4">
                <div className="mb-3 flex items-center gap-2">
                  <Target className="h-4 w-4 text-purple-600" />
                  <h4 className="text-sm font-semibold text-slate-900">
                    Metas de Venta
                  </h4>
                </div>

                {/* Daily Goal */}
                <div className="space-y-1">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-slate-600">Diaria</span>
                    <span
                      className={`font-semibold ${dailyProgress >= 100 ? 'text-green-600' : 'text-orange-600'}`}
                    >
                      ${goals.dailyActual.toLocaleString()} / $
                      {goals.dailyGoal.toLocaleString()}
                    </span>
                  </div>
                  <div className="h-2 w-full rounded-full bg-slate-200">
                    <div
                      className={`h-2 rounded-full ${dailyProgress >= 100 ? 'bg-yellow-500' : 'bg-yellow-500'}`}
                      style={{ width: `${Math.min(dailyProgress, 100)}%` }}
                    />
                  </div>
                  <p className="text-right text-xs text-slate-500">
                    {dailyProgress.toFixed(1)}%
                  </p>
                </div>

                {/* Monthly Goal */}
                <div className="space-y-1">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-slate-600">Mensual</span>
                    <span
                      className={`font-semibold ${monthlyProgress >= 100 ? 'text-green-600' : 'text-orange-600'}`}
                    >
                      ${goals.monthlyActual.toLocaleString()} / $
                      {goals.monthlyGoal.toLocaleString()}
                    </span>
                  </div>
                  <div className="h-2 w-full rounded-full bg-slate-200">
                    <div
                      className={`h-2 rounded-full ${monthlyProgress >= 100 ? 'bg-yellow-500' : 'bg-yellow-500'}`}
                      style={{ width: `${Math.min(monthlyProgress, 100)}%` }}
                    />
                  </div>
                  <p className="text-right text-xs text-slate-500">
                    {monthlyProgress.toFixed(1)}%
                  </p>
                </div>

                {/* Annual Goal */}
                <div className="space-y-1">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-slate-600">Anual</span>
                    <span
                      className={`font-semibold ${annualProgress >= 100 ? 'text-green-600' : 'text-orange-600'}`}
                    >
                      ${goals.annualActual.toLocaleString()} / $
                      {goals.annualGoal.toLocaleString()}
                    </span>
                  </div>
                  <div className="h-2 w-full rounded-full bg-slate-200">
                    <div
                      className={`h-2 rounded-full ${annualProgress >= 100 ? 'bg-yellow-500' : 'bg-yellow-500'}`}
                      style={{ width: `${Math.min(annualProgress, 100)}%` }}
                    />
                  </div>
                  <p className="text-right text-xs text-slate-500">
                    {annualProgress.toFixed(1)}%
                  </p>
                </div>
              </div>
            </div>
          )
        })}
      </div>

      {/* Consolidated Metrics */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
        <div className="rounded-lg border border-slate-200 bg-white p-6">
          <div className="mb-2 flex items-center gap-3">
            <div className="rounded-lg bg-green-50 p-2">
              <DollarSign className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-slate-600">Ventas Totales</p>
              <p className="text-2xl font-semibold text-slate-900">$436,000</p>
            </div>
          </div>
        </div>

        <div className="rounded-lg border border-slate-200 bg-white p-6">
          <div className="mb-2 flex items-center gap-3">
            <div className="rounded-lg bg-primary/10 p-2">
              <TrendingUp className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="text-sm text-slate-600">Utilidad Total</p>
              <p className="text-2xl font-semibold text-slate-900">$125,900</p>
            </div>
          </div>
        </div>

        <div className="rounded-lg border border-slate-200 bg-white p-6">
          <div className="mb-2 flex items-center gap-3">
            <div className="rounded-lg bg-purple-50 p-2">
              <Users className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-slate-600">Clientes Totales</p>
              <p className="text-2xl font-semibold text-slate-900">1,195</p>
            </div>
          </div>
        </div>

        <div className="rounded-lg border border-slate-200 bg-white p-6">
          <div className="mb-2 flex items-center gap-3">
            <div className="rounded-lg bg-orange-50 p-2">
              <Package className="h-5 w-5 text-orange-600" />
            </div>
            <div>
              <p className="text-sm text-slate-600">Productos Vendidos</p>
              <p className="text-2xl font-semibold text-slate-900">3,550</p>
            </div>
          </div>
        </div>
      </div>

      {/* Sales Goals vs Real Performance */}
      <div className="rounded-lg border border-slate-200 bg-white p-6">
        <h3 className="mb-4 font-semibold text-slate-900">
          Metas vs Ventas Reales (Mes Actual)
        </h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={goalsComparison}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="branch" stroke="#64748b" />
            <YAxis stroke="#64748b" />
            <Tooltip
              contentStyle={{
                backgroundColor: '#fff',
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
              }}
            />
            <Legend />
            <Bar
              dataKey="Meta"
              fill="#94a3b8"
              name="Meta"
              radius={[4, 4, 0, 0]}
            />
            <Bar dataKey="Real" name="Real" radius={[4, 4, 0, 0]}>
              {goalsComparison.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={entry.Real >= entry.Meta ? '#10b981' : '#f59e0b'}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Sales Comparison Chart */}
      <div className="rounded-lg border border-slate-200 bg-white p-6">
        <h3 className="mb-4 font-semibold text-slate-900">
          Comparativo de Ventas por Sucursal
        </h3>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={salesComparison}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="month" stroke="#64748b" />
            <YAxis stroke="#64748b" />
            <Tooltip
              contentStyle={{
                backgroundColor: '#fff',
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
              }}
            />
            <Legend />
            <Line
              type="monotone"
              dataKey="Centro"
              stroke="#3b82f6"
              strokeWidth={2}
              name="Centro"
              dot={{ r: 4 }}
            />
            <Line
              type="monotone"
              dataKey="Norte"
              stroke="#10b981"
              strokeWidth={2}
              name="Norte"
              dot={{ r: 4 }}
            />
            <Line
              type="monotone"
              dataKey="Sur"
              stroke="#f59e0b"
              strokeWidth={2}
              name="Sur"
              dot={{ r: 4 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Profit Comparison Chart */}
      <div className="rounded-lg border border-slate-200 bg-white p-6">
        <h3 className="mb-4 font-semibold text-slate-900">
          Comparativo de Utilidad por Sucursal
        </h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={profitComparison}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="month" stroke="#64748b" />
            <YAxis stroke="#64748b" />
            <Tooltip
              contentStyle={{
                backgroundColor: '#fff',
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
              }}
            />
            <Legend />
            <Bar
              dataKey="Centro"
              fill="#3b82f6"
              name="Centro"
              radius={[4, 4, 0, 0]}
            />
            <Bar
              dataKey="Norte"
              fill="#10b981"
              name="Norte"
              radius={[4, 4, 0, 0]}
            />
            <Bar
              dataKey="Sur"
              fill="#f59e0b"
              name="Sur"
              radius={[4, 4, 0, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Top Products by Branch */}
      <div className="rounded-lg border border-slate-200 bg-white p-6">
        <h3 className="mb-4 font-semibold text-slate-900">
          Productos Más Vendidos por Sucursal
        </h3>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          {branches.map((branch) => {
            const branchProducts = topProductsByBranch.filter(
              (p) => p.branch === branch.name.replace('Sucursal ', ''),
            )
            return (
              <div key={branch.id}>
                <h4 className="mb-3 font-medium text-slate-900">
                  {branch.name}
                </h4>
                <div className="space-y-2">
                  {branchProducts.map((product, idx) => (
                    <div
                      key={idx}
                      className="flex items-center justify-between rounded-lg bg-slate-50 p-3"
                    >
                      <div>
                        <p className="text-sm font-medium text-slate-900">
                          {product.product}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-semibold text-primary">
                          {product.units} uds
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* Goals Configuration Modal */}
      <Dialog open={showGoalsModal} onOpenChange={setShowGoalsModal}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-4xl">
          <DialogHeader>
            <DialogTitle>Configurar Metas de Venta</DialogTitle>
            <DialogDescription className="sr-only">
              Configurar las metas diarias, mensuales y anuales de cada sucursal
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-6">
            {editingGoals.map((goal, index) => (
              <div
                key={goal.branch}
                className="rounded-lg border border-slate-200 p-4"
              >
                <h3 className="mb-4 font-semibold text-slate-900">
                  {goal.branch}
                </h3>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">
                      Meta Diaria
                    </label>
                    <Input
                      type="number"
                      value={goal.dailyGoal}
                      onChange={(e) => {
                        const updated = [...editingGoals]
                        updated[index].dailyGoal =
                          parseFloat(e.target.value) || 0
                        setEditingGoals(updated)
                      }}
                      placeholder="0.00"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">
                      Meta Mensual
                    </label>
                    <Input
                      type="number"
                      value={goal.monthlyGoal}
                      onChange={(e) => {
                        const updated = [...editingGoals]
                        updated[index].monthlyGoal =
                          parseFloat(e.target.value) || 0
                        setEditingGoals(updated)
                      }}
                      placeholder="0.00"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">
                      Meta Anual
                    </label>
                    <Input
                      type="number"
                      value={goal.annualGoal}
                      onChange={(e) => {
                        const updated = [...editingGoals]
                        updated[index].annualGoal =
                          parseFloat(e.target.value) || 0
                        setEditingGoals(updated)
                      }}
                      placeholder="0.00"
                    />
                  </div>
                </div>
              </div>
            ))}

            <div className="flex gap-3">
              <Button onClick={handleSaveGoals} className="flex-1" size="lg">
                Guardar Metas
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowGoalsModal(false)}
                className="flex-1"
                size="lg"
              >
                Cancelar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
